import React, { useState, useEffect, useLayoutEffect, useCallback, useRef, useMemo } from 'react';
import { getStatistics, exportStatistics, getMonthlyTicketCounts, TicketStatistics } from '../services/api';
import { formatBackendError } from '../utils/formatError';
import { getChatbotAnalytics } from '../services/chatbotApi';
import type { ChatbotAnalytics } from '../services/chatbotApi';
import { ChatbotAnalyticsCard } from './StatistikSeite/ChatbotAnalyticsCard';
import { TicketaufkommenCard, Aggregation } from './StatistikSeite/TicketaufkommenCard';
import { VolumenJeKategorieCard } from './StatistikSeite/VolumenJeKategorieCard';
import { ReanalyseVerlaufCard } from './StatistikSeite/ReanalyseVerlaufCard';
import { DateFilterPills } from './StatistikSeite/DateFilterPills';
import { DateFilterRangeReadout } from './StatistikSeite/DateFilterRangeReadout';
import { CustomRangeModal } from './StatistikSeite/CustomRangeModal';
import { monthStart, monthEnd, isAdjacentMonth } from './StatistikSeite/DateFilterRangeReadout';
import { RueckgaengigToast } from './RueckgaengigToast';
import type { TimeBin } from './StatistikSeite/charts/KalenderwocheBarChart';
import { theme } from '../theme';
import { CATEGORY_KEYS } from '../constants';
import { useCategoryMeta } from '../context/ClassificationSchemaContext';
import { usePaletteMode } from '../context/PaletteModeContext';
import { useChatbotScope } from '../context/ChatbotScopeContext';
import { BentoCard, NavyKpiCard, DownloadIcon, ExcelIcon, PdfPageIcon, ModeSwapIcon, ChevronToggleButton, CategoryVocabularyMissingNotice } from './shared';
import { SecondaryAxisPills } from './SecondaryAxisPills';
import {
    PieChart, Pie, Cell, Sector, Tooltip, ResponsiveContainer,
} from 'recharts';

// <purpose>Executive-facing statistics page — KPIs, distributions, rejection analysis.</purpose>
// <dependencies>getStatistics API, Recharts, shared components (BentoCard, NavyKpiCard)</dependencies>
// <constraints>Layout stability: all rows use minHeight. No shift on filter change or empty states.</constraints>

interface StatisticsProps {
    chatbotEnabled?: boolean;
}

const DATE_FIELD_OPTIONS = [
    { value: 'received_at', label: 'Empfangsdatum' },
    { value: 'created_at', label: 'Erstelldatum' },
];

// Task 28 (PDF Page 1 KPI cards) will surface avgProcessingSeconds — keep until then.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const formatDuration = (seconds: number | null | undefined): string => {
    if (seconds == null || seconds <= 0) return '–';
    const m = Math.floor(seconds / 60);
    const s = Math.round(seconds % 60);
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

const StatistikSeite: React.FC<StatisticsProps> = ({ chatbotEnabled = false }) => {
    // Role-aware category meta — VNB gets marktprozess ids; gMSB falls back to K0–K4
    const cm = useCategoryMeta();
    const { mode: statPalette } = usePaletteMode();
    const { scope: chatbotScope } = useChatbotScope();
    // Why: heatmap needs all categories present in flow data to build grid axes.
    // RECONCILIATION surface, deliberately — cm.categoryKeys, never performanceCategoryKeys: the
    // rejection flow's whole subject is tickets MOVING between categories, and an unclassified value is
    // both a legitimate origin ("was unclassified, became K2") and a legitimate destination. Drop
    // it and those transitions vanish from the grid with no trace that anything was omitted.
    // VNB: schema marktprozess ids (no unclassified value in that axis). gMSB: K0–K4 + KU, from the served
    // vocabulary once delivered and from the CATEGORY_KEYS literal until then — the same set.
    const heatmapCategories = cm.categoryLabelsFromData
        ? cm.categoryKeys
        : [...CATEGORY_KEYS];
    const [stats, setStats] = useState<TicketStatistics | null>(null);
    const [loading, setLoading] = useState(true);
    // Rolling 12-month pill selection — the current month is the seed default (used verbatim
    // whenever the tenant has no ticket anywhere in the rolling window), immediately superseded
    // once the monthly-counts fetch below resolves to the newest month that actually HAS tickets —
    // landing on a guaranteed-empty "current month" made a quiet tenant look broken on first load.
    const [selectedMonths, setSelectedMonths] = useState<string[]>(() => {
        const now = new Date();
        const yyyy = now.getFullYear();
        const mm = (now.getMonth() + 1).toString().padStart(2, '0');
        return [`${yyyy}-${mm}`];
    });
    // Guards the auto-default to ONE application, off the first monthly-counts resolution — a
    // later dateField switch refetches this same endpoint and must never silently override a
    // month the operator has since picked.
    const monthDefaultAppliedRef = useRef(false);
    const [customRangeModalOpen, setCustomRangeModalOpen] = useState(false);
    // One-directional collide coupling: opening H-G-C drops its overlay DOWN over the chatbot
    // section (collision) → collapse the card. Opening the card grows it SIDEWAYS (no collision)
    // → leave H-G-C alone. (Was a single last-open-wins enum; that force-collapsed both ways.)
    const [reasonCodesOverlayOpen, setReasonCodesOverlayOpen] = useState(false);
    const [chatbotExpanded, setChatbotExpanded] = useState(false);
    // Custom range from the overflow modal (older than 12 months). When set, overrides pill selection.
    const [customRange, setCustomRange] = useState<{ start: string; end: string } | null>(null);
    const [dateField, setDateField] = useState('received_at');
    const [error, setError] = useState<string | null>(null);
    const [exporting, setExporting] = useState<'xlsx' | 'pdf' | null>(null);
    // WCAG 2.2.1 / DGUV Ch.9 — long export (PDF render can take 5–15s on a year of data)
    // must be cancellable. While exporting, the same circular button becomes the Abbrechen
    // affordance — click on '…' aborts the in-flight HTTP request via AbortSignal.
    const exportAbortRef = useRef<AbortController | null>(null);
    const [showFormats, setShowFormats] = useState(false);
    // Plan #2: KPI cards default to 'aktiv' (un-archived rows only). Click the swap
    // icon on Eingegangen to flip to 'archiv' (archived rows split the same way).
    const [statsMode, setStatsMode] = useState<'aktiv' | 'archiv'>('aktiv');
    // displayedMode lags statsMode by the Morph+ dip (200ms) so titles/values cross-fade through
    // opacity 0 before the text content updates — prevents visible instant text-swap during the dip.
    const [displayedMode, setDisplayedMode] = useState<'aktiv' | 'archiv'>('aktiv');
    // swapping=true for the 200ms dip after statsMode changes — drives opacity 1→0 in first half.
    const [swapping, setSwapping] = useState(false);
    // Accumulated rotation multiplier — each click adds 360°; CSS transition animates the delta.
    const [spinKey, setSpinKey] = useState(0);
    const [activePie, setActivePie] = useState<number | null>(null);
    const [aggregation, setAggregation] = useState<Aggregation>('kw');
    // Why: fetchStatistics is memoized without `aggregation` in deps (to avoid double-fetch
    // when aggregation changes — the dedicated aggregation effect handles that case). Without
    // a ref, the closed-over aggregation stays at its initial value forever, so date-range
    // changes always refetched with 'kw'. Ref keeps it always-fresh without recreating closure.
    const aggregationRef = useRef<Aggregation>(aggregation);
    useEffect(() => { aggregationRef.current = aggregation; }, [aggregation]);
    // Tracks which aggregation produced the current `stats` payload. Differs from
    // `aggregation` during the brief window between tab-click and fetch completion.
    // TicketaufkommenCard reads this to suppress the wrong-data flash.
    const [statsAggregation, setStatsAggregation] = useState<Aggregation | null>(null);
    // Info toast state (no undo — plain info mode)
    const [infoToast, setInfoToast] = useState<{ visible: boolean; message: string }>({ visible: false, message: '' });
    const abortRef = useRef(0);

    const [chatbotAnalytics, setChatbotAnalytics] = useState<ChatbotAnalytics | null>(null);
    // Monthly counts fetched from dedicated endpoint (always 12-month window, independent of date filter)
    const [monthlyCounts, setMonthlyCounts] = useState<Record<string, number>>({});

    // Why: collapse export picker on clicks outside the page content (gutter areas)
    useEffect(() => {
        if (!showFormats) return;
        const handler = (e: MouseEvent) => {
            const el = e.target as HTMLElement;
            if (el.tagName === 'DIV' && !el.closest('[data-card]'))
                setShowFormats(false);
        };
        window.addEventListener('click', handler);
        return () => window.removeEventListener('click', handler);
    }, [showFormats]);

    // SIG 3 cross-fade: when statsMode changes, hold swapping=true for the Morph+ dip (opacity 1→0),
    // then snap displayedMode to new value and clear swapping (opacity 0→1 via CSS transition).
    useEffect(() => {
        if (displayedMode === statsMode) {
            if (swapping) setSwapping(false);
            return;
        }
        setSwapping(true);
        const t = setTimeout(() => {
            setDisplayedMode(statsMode);
            setSwapping(false);
        }, 200);
        return () => clearTimeout(t);
    }, [statsMode, displayedMode, swapping]);

    // Fetch monthly counts from dedicated endpoint — refetches whenever dateField changes
    useEffect(() => {
        let cancelled = false;
        getMonthlyTicketCounts(dateField).then(counts => {
            if (cancelled) return;
            setMonthlyCounts(counts);
            // Land on the newest month the tenant actually has tickets in, derived from this
            // same response — never a second fetch. Runs once; see the ref's own comment.
            if (!monthDefaultAppliedRef.current) {
                monthDefaultAppliedRef.current = true;
                const monthsWithTickets = Object.entries(counts)
                    .filter(([, count]) => count > 0)
                    .map(([month]) => month)
                    .sort();
                if (monthsWithTickets.length > 0) {
                    setSelectedMonths([monthsWithTickets[monthsWithTickets.length - 1]]);
                }
                // Else: no ticket anywhere in the window — the current-month seed default stands.
            }
        }).catch(err => console.error('monthly counts fetch failed', err));
        return () => { cancelled = true; };
    }, [dateField]);

    // Collapse adjacent months into contiguous ranges; non-adjacent months → multiple ranges.
    const dateRanges = useMemo((): Array<{start: Date; end: Date}> => {
        if (customRange) {
            return [{ start: new Date(customRange.start + 'T00:00:00'), end: new Date(customRange.end + 'T23:59:59.999') }];
        }
        if (selectedMonths.length === 0) return [];
        const sorted = [...selectedMonths].sort();
        const ranges: Array<{start: Date; end: Date}> = [];
        let currentStart = sorted[0];
        let currentEnd = sorted[0];
        for (let i = 1; i < sorted.length; i++) {
            if (isAdjacentMonth(currentEnd, sorted[i])) {
                currentEnd = sorted[i];
            } else {
                ranges.push({ start: monthStart(currentStart), end: monthEnd(currentEnd) });
                currentStart = sorted[i];
                currentEnd = sorted[i];
            }
        }
        ranges.push({ start: monthStart(currentStart), end: monthEnd(currentEnd) });
        return ranges;
    }, [selectedMonths, customRange]);

    // Canonical "has the selected date range changed?" identity — one source of truth for every effect
    // that refetches on range change (ticket stats + chatbot analytics), so the two can't drift.
    const dateRangesKey = useMemo(
        () => JSON.stringify(dateRanges.map(r => `${r.start.toISOString()}..${r.end.toISOString()}`)),
        [dateRanges],
    );

    const fetchStatistics = useCallback((ranges: Array<{start: Date; end: Date}>, agg?: Aggregation) => {
        setLoading(true);
        setError(null);
        const callId = ++abortRef.current;
        const effectiveAgg = agg ?? aggregationRef.current;
        (async () => {
            try {
                const data = await getStatistics(ranges, dateField, effectiveAgg);
                if (callId !== abortRef.current) return;
                if (data) {
                    setStats(data);
                    // Track which aggregation produced this stats payload — TicketaufkommenCard
                    // uses this to guard against rendering the new chart with old data during
                    // the brief aggregation-switch fetch window.
                    setStatsAggregation(effectiveAgg);
                }
                else setError('Keine Daten verfügbar oder kein Zugriff.');
            } catch (err: any) {
                if (callId !== abortRef.current) return;
                setError(formatBackendError(err, 'Laden der Statistiken'));
            } finally {
                if (callId === abortRef.current) setLoading(false);
            }
        })();
    // Why: aggregation intentionally omitted — `agg` param overrides it at call sites;
    // the separate aggregation-change effect handles refetch when aggregation state changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dateField]);

    // Refetch when month selection, custom range, or dateField changes.
    // Always fires — even for empty dateRanges (BE returns zeros; FE renders "Keine Daten" prompts).
    useEffect(() => {
        fetchStatistics(dateRanges);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchStatistics, dateRangesKey]);

    // Chatbot analytics — dedicated fetch that (unlike the piggyback in fetchStatistics) refetches on
    // BOTH scope flip AND date-range changes (mirrors the ticket refetch's dateRanges key). Never nulls
    // mid-flip → the card updates in place (layout-stable). null result → the section is simply absent.
    useEffect(() => {
        if (!chatbotEnabled || dateRanges.length === 0) { setChatbotAnalytics(null); return; }
        let cancelled = false;
        const fromStr = dateRanges[0].start.toISOString().split('T')[0];
        const toStr = dateRanges[dateRanges.length - 1].end.toISOString().split('T')[0];
        getChatbotAnalytics(fromStr, toStr, chatbotScope)
            .then(data => { if (!cancelled) setChatbotAnalytics(data); });
        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chatbotEnabled, chatbotScope, dateRangesKey]);

    // Re-fetch when aggregation changes (kw/q need different BE response shape)
    const prevAggregationRef = useRef(aggregation);
    useEffect(() => {
        if (prevAggregationRef.current === aggregation) return;
        prevAggregationRef.current = aggregation;
        fetchStatistics(dateRanges, aggregation);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [aggregation, fetchStatistics]);

    const handleExport = async (format: 'xlsx' | 'pdf') => {
        if (dateRanges.length === 0) {
            setInfoToast({ visible: true, message: 'Bitte mindestens einen Monat auswählen.' });
            return;
        }
        const ctrl = new AbortController();
        exportAbortRef.current = ctrl;
        setExporting(format);
        setError(null);
        try {
            await exportStatistics(dateRanges, dateField, format, aggregation, { signal: ctrl.signal });
        } catch (err: any) {
            // Why: AbortError on user-cancel is expected; only show 'Fehler' for real failures.
            if (err?.name !== 'CanceledError' && err?.name !== 'AbortError' && !ctrl.signal.aborted) {
                setError(formatBackendError(err, 'Exportieren'));
            }
        } finally {
            exportAbortRef.current = null;
            setExporting(null);
            setShowFormats(false);
        }
    };

    const handleCancelExport = () => {
        exportAbortRef.current?.abort();
    };

    // Why: stable key forces Recharts remount with fresh enter animation on filter change.
    // Must encode ONLY filter inputs (not data results like totalReceived) — including a
    // data-derived value caused filterHash to change when the async fetch resolved mid-animation,
    // remounting PieChart via key change and restarting the 600ms entry animation (visible stutter).
    const filterHash = `${selectedMonths.join(',')}-${customRange?.start ?? ''}-${dateField}`;

    const pieData = useMemo(() => {
        if (!stats) return [];
        // Categorization PERFORMANCE surface (the Kategorieverteilung donut): unclassified values are
        // dropped via the served evaluation marker, so a slice can only ever be a real
        // classification outcome. marktprozess ids for VNB; K0–K4 for gMSB, with or without a
        // served vocabulary — the pre-vocabulary floor already excluded the unclassified bucket, and the marker
        // is what keeps that true once the vocabulary arrives.
        return cm.performanceCategoryKeys
            .map(k => ({ name: k, value: stats.perPrimaryAxisCount[k] ?? 0, label: cm.label(k) }))
            .filter(d => d.value > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stats, cm.categoryLabelsFromData, cm.performanceCategoryKeys.join(',')]);

    const pieTotal = pieData.reduce((a, b) => a + b.value, 0);

    const reasonCodeData = useMemo(() => {
        if (!stats) return [];
        return Object.entries(stats.perSecondaryAxisCount)
            .sort((a, b) => b[1] - a[1])
            .map(([code, count]) => ({ code, count }));
    }, [stats]);

    const maxReasonCount = reasonCodeData.length > 0 ? reasonCodeData[0].count : 1;

    // Width must accommodate the pill's actual face — SecondaryAxisPills' de-slugged form, the
    // SAME accessor the row renders through, never a parallel width estimate.
    const pillWidth = useMemo(() => {
        const maxLen = reasonCodeData.reduce((m, d) =>
            Math.max(m, cm.secondaryAxisText(d.code).length), 0);
        return Math.max(90, Math.min(180, maxLen * 6.5 + 24));
    }, [reasonCodeData, cm]);

    // Measure the rest-rows' natural height so the reveal opens to an EXACT fit and the chevron can ride
    // its bottom. A ResizeObserver keeps it current across late reflows (font swap-in, data change,
    // viewport resize) — a one-shot measure went stale and clipped the last row. jsdom → 0 (tests unaffected).
    const reasonOverlayRef = useRef<HTMLDivElement>(null);
    const [reasonOverlayH, setReasonOverlayH] = useState(0);
    useLayoutEffect(() => {
        const el = reasonOverlayRef.current;
        if (!el) return;
        const measure = () => setReasonOverlayH(el.offsetHeight);
        measure();
        if (typeof ResizeObserver === 'undefined') return; // jsdom guard
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        return () => ro.disconnect();
    }, [reasonCodeData, cm.categoryLabelsFromData]);

    const dailyVolumeData = useMemo(() => {
        if (!stats?.dailyVolume?.length) return [];
        return stats.dailyVolume.map(d => ({
            date: new Date(d.ticketDate).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' }),
            count: d.count,
        }));
    }, [stats]);

    // Why: both kw and q consume the same field — BE segments by requestedAggregation,
    // so only one is populated at a time. Aggregate across categories into total-per-bin.
    const periodVolume = useMemo((): TimeBin[] => {
        if (!stats?.perPrimaryAxisVolumeByPeriod?.length) return [];
        const byBin = new Map<string, { label: string; count: number; binStart: string }>();
        for (const b of stats.perPrimaryAxisVolumeByPeriod) {
            if (!byBin.has(b.binLabel)) {
                byBin.set(b.binLabel, { label: b.binLabel, count: 0, binStart: b.binStart });
            }
            byBin.get(b.binLabel)!.count += b.count;
        }
        return Array.from(byBin.values())
            .sort((a, b) => new Date(a.binStart).getTime() - new Date(b.binStart).getTime());
    }, [stats?.perPrimaryAxisVolumeByPeriod]);

    // Why: heatmap grid needs a lookup matrix from the raw flow pairs
    const heatmapData = useMemo(() => {
        if (!stats?.rejectionFlow?.length) return null;
        const matrix: Record<string, Record<string, number>> = {};
        const activeFrom = new Set<string>();
        const activeTo = new Set<string>();
        let maxCount = 0;
        for (const item of stats.rejectionFlow) {
            if (!matrix[item.fromPrimaryAxis]) matrix[item.fromPrimaryAxis] = {};
            matrix[item.fromPrimaryAxis][item.toPrimaryAxis] = (matrix[item.fromPrimaryAxis][item.toPrimaryAxis] ?? 0) + item.count;
            const val = matrix[item.fromPrimaryAxis][item.toPrimaryAxis];
            if (val > maxCount) maxCount = val;
            activeFrom.add(item.fromPrimaryAxis);
            activeTo.add(item.toPrimaryAxis);
        }
        // Only show categories that appear in the data
        const rows = heatmapCategories.filter(c => activeFrom.has(c));
        const cols = heatmapCategories.filter(c => activeTo.has(c));
        return { matrix, rows, cols, maxCount };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stats, cm.categoryLabelsFromData, cm.categoryKeys.join(',')]);

    const kpiValue: React.CSSProperties = {
        fontSize: 28, fontWeight: 700, color: theme.colors.white,
    };

    const kpiValueLarge: React.CSSProperties = {
        fontSize: 42, fontWeight: 700, color: theme.colors.white, lineHeight: 1.1,
    };

    const subStat: React.CSSProperties = {
        fontSize: 11, color: 'rgba(255,255,255,0.7)', fontFamily: theme.font.family, marginTop: 2,
    };

    // Reason code: fixed-width lane keeps bar origins aligned, pill fits text and centers within
    const rcLaneStyle: React.CSSProperties = {
        width: pillWidth, flexShrink: 0,
        display: 'flex', justifyContent: 'center',
    };
    // Shared row renderer for top-3 (in-card) and tail (overlay). Extracted verbatim from
    // the pre-Task-2 inline map — pixel-identical to the previous in-card rendering.
    // Phase 1's card chevron should reuse this affordance when a second caller exists.
    const renderReasonRow = (item: { code: string; count: number }) => (
        <div key={item.code} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={rcLaneStyle}>
                <SecondaryAxisPills codes={[item.code]} maxVisible={1} variant="stats" />
            </div>
            <div style={{ flex: 1, height: 14, backgroundColor: theme.colors.bgLight, borderRadius: 7, overflow: 'hidden' }}>
                <div style={{
                    height: '100%', width: `${(item.count / maxReasonCount) * 100}%`,
                    backgroundColor: theme.colors.navyMid, borderRadius: 7, transition: 'width 0.5s ease',
                }} />
            </div>
            <span style={{ fontSize: 11, color: theme.colors.textSecondary, minWidth: 20, textAlign: 'right' }}>
                {item.count}
            </span>
        </div>
    );

    const AGGREGATION_OPTIONS: { value: Aggregation; label: string }[] = [
        { value: 'daily', label: 'Tag' },
        { value: 'kw', label: 'Kalenderwoche' },
        { value: 'q', label: 'Quartal' },
    ];

    // Donut hover: render the active slice slightly enlarged for emphasis
    const renderActiveShape = (props: any) => {
        const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } = props;
        return (
            <Sector
                cx={cx} cy={cy}
                innerRadius={innerRadius - 2}
                outerRadius={outerRadius + 6}
                startAngle={startAngle} endAngle={endAngle}
                fill={fill}
                stroke="none"
            />
        );
    };

    // Donut tooltip — name-only (count/% live in the legend row that highlights in sync)
    const DonutTooltipContent = ({ active, payload }: any) => {
        if (!active || !payload?.length) return null;
        const d = payload[0].payload as { name: string; value: number; label: string };
        const color = cm.color(d.name);
        return (
            <div style={{
                background: '#fff', border: `1px solid ${theme.colors.borderLight}`,
                borderRadius: 6, padding: '5px 10px', fontSize: 11,
                fontFamily: theme.font.family, boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
                display: 'flex', alignItems: 'center', gap: 6, pointerEvents: 'none',
            }}>
                <span style={{ width: 8, height: 8, borderRadius: 2, backgroundColor: color, flexShrink: 0, display: 'inline-block' }} />
                <span style={{ color: theme.colors.navy, fontWeight: 600 }}>{d.label}</span>
            </div>
        );
    };

    return (
        <div>
            <h1 className="visually-hidden">Statistik</h1>
            {/* A0 Row 1: Datumsfeld (align-left) + Zeitraum pills (align-right), no labels */}
            <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '6px 0', marginBottom: 8,
            }}>
                <div style={{ display: 'flex', alignItems: 'center', fontSize: 11 }}>
                    <select
                        aria-label="Datumsfeld für Statistik"
                        value={dateField}
                        onChange={e => setDateField(e.target.value)}
                        style={{
                            padding: '4px 8px', fontSize: 11, fontFamily: theme.font.family,
                            border: `1px solid ${theme.colors.borderLight}`,
                            borderRadius: theme.borderRadius.sm, outline: 'none', cursor: 'pointer',
                            color: theme.colors.navy, backgroundColor: theme.colors.white,
                        }}
                    >
                        {DATE_FIELD_OPTIONS.map(o => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                    </select>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', fontSize: 11 }}>
                    <div style={{ display: 'inline-flex', gap: 3 }}>
                        {AGGREGATION_OPTIONS.map(opt => {
                            const isActive = aggregation === opt.value;
                            return (
                                <button
                                    key={opt.value}
                                    onClick={() => setAggregation(opt.value)}
                                    aria-pressed={isActive}
                                    style={{
                                        padding: '4px 10px',
                                        fontSize: 11,
                                        borderRadius: 12,
                                        border: 'none',
                                        cursor: 'pointer',
                                        background: isActive ? theme.colors.navy : '#f1f2f6',
                                        color: isActive ? 'white' : theme.colors.navy,
                                        fontWeight: isActive ? 700 : 400,
                                        fontFamily: theme.font.family,
                                        transition: 'background 0.15s ease',
                                    }}
                                >
                                    {opt.label}
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* A0 Row 2: 12 rolling-month pills + range readout — stable height, no layout shift */}
            <div style={{ padding: '8px 0', marginBottom: theme.spacing.md }}>
                <DateFilterPills
                    selectedMonths={selectedMonths}
                    onChange={months => { setSelectedMonths(months); setCustomRange(null); }}
                    ticketCountsByMonth={monthlyCounts}
                    onOverflowClick={() => setCustomRangeModalOpen(true)}
                />
                <DateFilterRangeReadout selectedMonths={customRange ? [] : selectedMonths} />
                {customRange && (
                    <p style={{ fontSize: 10, color: theme.colors.textSecondary, margin: '6px 0 0 0', fontFamily: 'monospace' }}>
                        Zeitraum: {customRange.start} – {customRange.end}
                        <button
                            onClick={() => setCustomRange(null)}
                            style={{ marginLeft: 8, fontSize: 10, cursor: 'pointer', background: 'none', border: 'none', color: theme.colors.textSecondary, textDecoration: 'underline', fontFamily: theme.font.family }}
                        >
                            ✕ zurücksetzen
                        </button>
                    </p>
                )}
            </div>

            <CustomRangeModal
                open={customRangeModalOpen}
                onClose={() => setCustomRangeModalOpen(false)}
                onApply={(start, end) => {
                    setCustomRange({ start, end });
                    setSelectedMonths([]); // clear pills when custom range is active
                }}
            />

            {/* Loading indicator deliberately omitted — any layout-affecting placement causes a
                jerk between filter clicks (panel pushed down then back up). The data refresh is
                fast enough that the user perceives stale-then-new data without needing a textual
                cue. If a signal becomes necessary, prefer an absolute-positioned dot overlay over
                anything that affects layout. */}
            {error && (
                <div role="alert" style={{
                    color: theme.colors.error, textAlign: 'center', fontSize: 14,
                    padding: theme.spacing.sm, marginBottom: theme.spacing.sm,
                }}>
                    {error}
                </div>
            )}

            {/* Empty selection state — no month selected and no custom range */}
            {!loading && dateRanges.length === 0 && (
                <p style={{ color: theme.colors.textSecondary, textAlign: 'center', fontSize: 14, marginTop: 40, fontFamily: theme.font.family }}>
                    Keine Daten im gewählten Zeitraum.
                </p>
            )}

            {/* Panel stays mounted across refetches so card-local state survives.
                Hidden when selection is empty (defers to the 'Keine Daten' line above). */}
            {stats && dateRanges.length > 0 && (
                <>
                    {/* ═══ Row 1: KPI cards + Category Pie ═══ */}
                    <div style={{ display: 'flex', gap: theme.spacing.lg, marginBottom: theme.spacing.lg, flexWrap: 'wrap' }}>
                        {/* Left: KPI stack — defaults to 'aktiv' (un-archived); ModeSwapIcon flips
                            the whole stack to 'archiv' (archived rows split the same way).
                            displayedMode lags statsMode by the 200ms Morph+ dip for cross-fade; muted
                            prop uses statsMode directly so the bg color transition starts immediately. */}
                        <div style={{ flex: '2 1 280px', minWidth: 260, display: 'flex', flexDirection: 'column', gap: theme.spacing.md }}>
                            {(() => {
                                // isArchiv drives content — uses lagged displayedMode so text doesn't
                                // swap until it's invisible (mid-fade). bg mute uses live statsMode.
                                const isArchiv = displayedMode === 'archiv';
                                const bgMuted = statsMode === 'archiv';
                                // C3: headline = full period cohort (active + archived); both modes.
                                const headTotal = stats.totalReceived;
                                const unprocessed = isArchiv ? stats.archivedUnprocessed : stats.totalUnprocessed;
                                const offen = isArchiv ? stats.archivedOffen : stats.totalOffen;
                                const approved = isArchiv ? stats.archivedApproved : stats.totalApproved;
                                // C1: the "Genehmigt" card shows totalApproved (explicit approvals) only.
                                // The "+ N kopiert" sub-line is an additive display note showing the
                                // copy-only population (approved_at IS NULL) beside it — it feeds no rate,
                                // just a second count next to the card's number. Active-mode only
                                // (archiv has no copy-approved field); hidden when zero. Older BE omits the field.
                                const copyApproved = stats.totalCopyApproved ?? 0;
                                const swapTooltip = bgMuted ? 'Aktive Statistiken anzeigen' : 'Archiv-Statistiken anzeigen';
                                const archiveText = bgMuted ? theme.colors.mode.archived[statPalette].text : theme.colors.white;
                                const archiveSub = bgMuted ? (statPalette === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(0,13,102,0.85)') : 'rgba(255,255,255,0.7)'; // light: 0.85 navy on greige ≈ 5:1 (WCAG-AA at 11px)
                                // fadeStyle: opacity dips to 0 then restores — wraps all
                                // swappable text nodes (title handled inside NavyKpiCard via title prop).
                                const fadeStyle: React.CSSProperties = {
                                    opacity: swapping ? 0 : 1,
                                    transition: 'opacity 0.2s ease',
                                };
                                return (
                                    <>
                                        <NavyKpiCard
                                            title="Eingegangen"
                                            style={{ flex: 'none' }}
                                            muted={bgMuted}
                                            titleStyle={fadeStyle}
                                            headerAction={
                                                <button
                                                    onClick={() => {
                                                        setSpinKey(k => k + 1);
                                                        setStatsMode(m => m === 'archiv' ? 'aktiv' : 'archiv');
                                                    }}
                                                    title={swapTooltip}
                                                    aria-label={swapTooltip}
                                                    style={{
                                                        background: 'none', border: 'none', cursor: 'pointer',
                                                        width: 40, height: 40, display: 'inline-flex',
                                                        alignItems: 'center', justifyContent: 'center',
                                                        color: bgMuted ? theme.colors.mode.archived[statPalette].text : 'rgba(255,255,255,0.85)',
                                                    }}
                                                >
                                                    {/* 360° spin per click: spinKey accumulates; CSS transition
                                                        animates the delta so each click is a full new rotation. */}
                                                    <span style={{
                                                        display: 'inline-flex',
                                                        transform: `rotate(${spinKey * 360}deg)`,
                                                        transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                                                    }}>
                                                        <ModeSwapIcon size={16} />
                                                    </span>
                                                </button>
                                            }
                                        >
                                            <span style={fadeStyle} title="Ticketaufkommen: aktiv + archiviert im Zeitraum"><div style={{ ...kpiValueLarge, color: archiveText }}>{headTotal}</div></span>
                                            {/* Always reserve the sub-stat row so the card height doesn't
                                                jump on mode swap (UI Layout Stability Rule). The row is
                                                always rendered; content is the same in both modes (C3). */}
                                            <div style={{ ...subStat, color: archiveSub }}>
                                                <span style={fadeStyle}>
                                                    {`${stats.totalReceived - stats.totalArchived} aktiv · ${stats.totalArchived} archiviert`}
                                                </span>
                                            </div>
                                        </NavyKpiCard>
                                        <div style={{ display: 'flex', gap: theme.spacing.sm, flexWrap: 'wrap' }}>
                                            {/* All three small cards carry the SAME reserved single-line
                                                sub-row: one card with a sub-row and two without pushed the
                                                Genehmigt number out of line with its siblings, and the old
                                                two-line wrapping text overflowed its reservation so the
                                                archiv flip still jerked (UI Layout Stability Rule). The
                                                copy-approval explanation moved to hover — the sub-line
                                                carries only the count, which cannot wrap. */}
                                            <NavyKpiCard
                                                title={isArchiv ? 'Ungeklärt' : 'Uploads'}
                                                style={{ flex: '1 1 80px', minWidth: 80 }}
                                                muted={bgMuted}
                                                titleStyle={fadeStyle}>
                                                <div style={{ ...kpiValue, color: archiveText }}>{unprocessed}</div>
                                                <div style={{ ...subStat, minHeight: 16, whiteSpace: 'nowrap' }} />
                                            </NavyKpiCard>
                                            <NavyKpiCard
                                                title={isArchiv ? 'Liegengeblieben' : 'Offen'}
                                                style={{ flex: '1 1 80px', minWidth: 80 }}
                                                muted={bgMuted}
                                                titleStyle={fadeStyle}>
                                                <div style={{ ...kpiValue, color: archiveText }}>{offen}</div>
                                                <div style={{ ...subStat, minHeight: 16, whiteSpace: 'nowrap' }} />
                                            </NavyKpiCard>
                                            <NavyKpiCard
                                                title={isArchiv ? 'Erledigt' : 'Genehmigt'}
                                                style={{ flex: '1 1 80px', minWidth: 80 }}
                                                muted={bgMuted}
                                                titleStyle={fadeStyle}>
                                                <div style={{ ...kpiValue, color: archiveText }}>{approved}</div>
                                                <div
                                                    style={{ ...subStat, color: archiveSub, minHeight: 16, whiteSpace: 'nowrap' }}
                                                >
                                                    <span style={fadeStyle}>
                                                        {(!isArchiv && copyApproved > 0) ? `+ ${copyApproved} kopiert` : ''}
                                                    </span>
                                                </div>
                                            </NavyKpiCard>
                                        </div>
                                    </>
                                );
                            })()}
                        </div>

                        {/* Right: Category pie — legend left syncs with donut hover (bidirectional) */}
                        <BentoCard title={`${cm.categoryNounCompound}verteilung`} style={{ flex: '1 1 280px', minWidth: 260 }}
                            contentStyle={{ padding: '8px 12px' }}>
                            {cm.vocabularyMissing ? (
                                <CategoryVocabularyMissingNotice />
                            ) : pieTotal > 0 ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                    {/* Left: legend — hover syncs with slice highlight */}
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flexShrink: 0 }}>
                                        {pieData.map((d, i) => {
                                            const pct = Math.round((d.value / pieTotal) * 100);
                                            const isActive = activePie === i;
                                            return (
                                                <div
                                                    key={d.name}
                                                    onMouseEnter={() => setActivePie(i)}
                                                    onMouseLeave={() => setActivePie(null)}
                                                    style={{
                                                        display: 'flex', alignItems: 'center', gap: 6,
                                                        fontSize: 11, fontFamily: theme.font.family,
                                                        cursor: 'default', borderRadius: 4, padding: '1px 4px',
                                                        background: isActive ? theme.colors.bgLight : 'transparent',
                                                        fontWeight: isActive ? 700 : undefined,
                                                        transition: 'background 0.15s',
                                                    }}
                                                >
                                                    <div style={{
                                                        width: 8, height: 8, borderRadius: 2, flexShrink: 0,
                                                        backgroundColor: cm.color(d.name),
                                                    }} />
                                                    <span style={{ color: theme.colors.navy, fontWeight: isActive ? 700 : 600, minWidth: 22 }}>{cm.abbr(d.name)}</span>
                                                    <span style={{ color: theme.colors.navy, fontWeight: 600, minWidth: 16, textAlign: 'right' }}>{d.value}</span>
                                                    <span style={{ color: theme.colors.textSecondary, fontSize: 10, minWidth: 28, textAlign: 'right' }}>{pct}%</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                    {/* Right: donut — slice emphasis + tooltip on hover */}
                                    {/* data-stx-pie-key: exposes filterHash to tests so node-identity assertions
                                        can verify the key hasn't changed when only data (not filters) updates */}
                                    <div style={{ position: 'relative', flex: 1, minWidth: 120, height: 160 }} data-stx-pie-key={filterHash}>
                                        <ResponsiveContainer width="100%" height={160}>
                                            <PieChart key={filterHash}>
                                                <Pie data={pieData} dataKey="value" nameKey="name"
                                                    cx="50%" cy="50%" innerRadius={44} outerRadius={70}
                                                    paddingAngle={2} animationDuration={600}
                                                    isAnimationActive={true}
                                                    label={false} labelLine={false}
                                                    activeShape={renderActiveShape}
                                                    onMouseEnter={(_data: any, idx: number) => setActivePie(idx)}
                                                    onMouseLeave={(_data: any, _idx: number) => setActivePie(null)}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    {pieData.map(d => (
                                                        <Cell key={d.name} fill={cm.color(d.name)} stroke="none" />
                                                    ))}
                                                </Pie>
                                                <Tooltip
                                                    content={<DonutTooltipContent />}
                                                    wrapperStyle={{ zIndex: 10 }}
                                                />
                                            </PieChart>
                                        </ResponsiveContainer>
                                        {/* Center label */}
                                        <div style={{
                                            position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                                            textAlign: 'center', pointerEvents: 'none',
                                        }}>
                                            <div style={{ fontSize: 22, fontWeight: 700, color: theme.colors.navy }}>{pieTotal}</div>
                                            <div style={{ fontSize: 9, color: theme.colors.textSecondary }}>Gesamt</div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div style={{ minHeight: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.colors.textSecondary, fontSize: 13 }}>
                                    Keine {cm.categoryNounCompound}daten
                                </div>
                            )}
                        </BentoCard>
                    </div>

                    {/* ═══ Row 2: Ticket Volume (3-way toggle: Tag / KW / Quartal) ═══ */}
                    <div style={{ marginBottom: theme.spacing.lg }}>
                        <TicketaufkommenCard
                            dailyVolume={dailyVolumeData}
                            kwVolume={periodVolume}
                            qVolume={periodVolume}
                            aggregation={aggregation}
                            statsAggregation={statsAggregation}
                            onAggregationChange={setAggregation}
                            filterHash={filterHash}
                            rawCategoryData={stats?.perPrimaryAxisVolumeByPeriod}
                        />
                    </div>

                    {/* ═══ Row 3: Volumen (left, w/ Abl + Reanalyse stacked) + Häufigste (right) ═══ */}
                    <div style={{ display: 'flex', gap: theme.spacing.lg, marginBottom: theme.spacing.lg, flexWrap: 'wrap', position: 'relative', zIndex: 2 }}>
                        {/* LEFT column — Volumen hero, then the Abl+Reanalyse sub-row beneath it */}
                        <div style={{ flex: '4 1 360px', minWidth: 320, display: 'flex', flexDirection: 'column', gap: theme.spacing.md }}>
                            <VolumenJeKategorieCard
                                data={stats?.perPrimaryAxisVolumeByPeriod ?? []}
                                filterHash={filterHash}
                            />
                            {/* Abl + Reanalyse sub-row — MOVED verbatim from the old right column */}
                            <div style={{ display: 'flex', gap: theme.spacing.md }}>
                                <BentoCard title="Ablehnungsmuster" style={{ flex: '2 1 100px' }}
                                    contentStyle={{ paddingRight: theme.spacing.md + 10, paddingBottom: theme.spacing.md + 10 }}>
                                    {heatmapData ? (
                                        <div>
                                            {/* Column axis label */}
                                            <div style={{ display: 'flex', alignItems: 'flex-end', marginBottom: 2 }}>
                                                <div style={{ width: 32, flexShrink: 0 }} />
                                                <div style={{ fontSize: 9, color: theme.colors.textSecondary, fontFamily: theme.font.family, marginLeft: 2 }}>
                                                    → neu zugewiesen als
                                                </div>
                                            </div>
                                            {/* Column headers */}
                                            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
                                                <div style={{ width: 32, flexShrink: 0 }} />
                                                {heatmapData.cols.map(col => (
                                                    <div key={col} style={{
                                                        flex: 1, textAlign: 'center',
                                                        fontSize: 9, fontWeight: 600, color: theme.colors.navy,
                                                        fontFamily: theme.font.family,
                                                    }}>
                                                        {cm.abbr(col)}
                                                    </div>
                                                ))}
                                            </div>
                                            {/* Rows — fluid cells fill available width */}
                                            {heatmapData.rows.map(row => (
                                                <div key={row} style={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
                                                    <div style={{
                                                        width: 32, flexShrink: 0, textAlign: 'right', paddingRight: 4,
                                                        fontSize: 9, fontWeight: 600, color: theme.colors.navy,
                                                        fontFamily: theme.font.family,
                                                    }}>
                                                        {cm.abbr(row)}
                                                    </div>
                                                    {heatmapData.cols.map(col => {
                                                        const val = heatmapData.matrix[row]?.[col] ?? 0;
                                                        const intensity = val > 0 ? Math.max(0.12, val / heatmapData.maxCount) : 0;
                                                        return (
                                                            <div key={col} style={{
                                                                flex: 1, aspectRatio: '1.4', margin: 1,
                                                                borderRadius: 3,
                                                                backgroundColor: val > 0
                                                                    ? `rgba(0, 13, 102, ${intensity})`
                                                                    : theme.colors.bgLight,
                                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                fontSize: 10, fontWeight: val > 0 ? 600 : 400,
                                                                color: intensity > 0.5 ? theme.colors.white : theme.colors.navy,
                                                                fontFamily: theme.font.family,
                                                                transition: 'background-color 0.3s ease',
                                                            }}
                                                                title={val > 0 ? `${row} → ${col}: ${val} Ablehnungen` : undefined}
                                                            >
                                                                {val > 0 ? val : ''}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div style={{ minHeight: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.colors.textSecondary, fontSize: 12 }}>
                                            Keine Ablehnungsdaten
                                        </div>
                                    )}
                                </BentoCard>
                                <div style={{ flex: '1 1 80px', minWidth: 80 }}>
                                    <ReanalyseVerlaufCard data={stats?.reanalyseVerlauf} />
                                </div>
                            </div>
                        </div>

                        {/* RIGHT column — Häufigste Grund-Codes/Anliegen. ONE card that clips to 3 rows and
                            reveals to full height; it floats (absolute over a hidden spacer) so opening it
                            never pushes the chatbot section below down. */}
                        <div style={{ flex: '3 1 280px', minWidth: 240 }}>
                            <div style={{ position: 'relative' }}>
                                {reasonCodeData.length > 3 ? (
                                    <>
                                        {/* Footprint spacer — hidden collapsed copy (title + 3 rows) reserves the
                                            in-flow height so the absolute live card grows without a layout jump.
                                            aria-hidden + visibility:hidden keep its duplicated title/rows out of
                                            the a11y tree + tab order (axe/SRs skip it). */}
                                        <div aria-hidden="true" style={{ visibility: 'hidden' }}>
                                            <BentoCard title={`Häufigste ${cm.secondaryAxisNounPlural}`}>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                                    {reasonCodeData.slice(0, 3).map(renderReasonRow)}
                                                </div>
                                            </BentoCard>
                                        </div>

                                        {/* Live card — absolute over the spacer. ONE BentoCard (one shadow, no seam);
                                            rows beyond 3 sit in an inner max-height reveal that grows the card. */}
                                        <div style={{ position: 'absolute', top: 0, left: 0, right: 0 }}>
                                            <BentoCard title={`Häufigste ${cm.secondaryAxisNounPlural}`}>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                                    {reasonCodeData.slice(0, 3).map(renderReasonRow)}
                                                </div>
                                                <div
                                                    id="stx-reasoncode-overlay"
                                                    data-stx-reasoncode-overlay
                                                    role="region"
                                                    aria-label={`Weitere ${cm.secondaryAxisNounPlural}`}
                                                    aria-hidden={!reasonCodesOverlayOpen}
                                                    style={{
                                                        maxHeight: reasonCodesOverlayOpen ? reasonOverlayH : 0,
                                                        overflow: 'hidden',
                                                        transition: 'max-height 0.42s cubic-bezier(0.4,0,0.2,1)',
                                                    }}
                                                >
                                                    <div ref={reasonOverlayRef} style={{ display: 'flex', flexDirection: 'column', gap: 6, paddingTop: 6, paddingBottom: 4 }}>
                                                        {reasonCodeData.slice(3).map(renderReasonRow)}
                                                    </div>
                                                </div>
                                            </BentoCard>
                                        </div>

                                        {/* Chevron — sibling (not clipped by the card); rides to the card's grown bottom. */}
                                        <ChevronToggleButton
                                            open={reasonCodesOverlayOpen}
                                            onClick={() => setReasonCodesOverlayOpen(o => !o)}
                                            ariaLabel={`Alle ${cm.secondaryAxisNounPlural} ${reasonCodesOverlayOpen ? 'ausblenden' : 'anzeigen'} (${reasonCodeData.length})`}
                                            ariaControls="stx-reasoncode-overlay"
                                            style={{
                                                position: 'absolute', left: '50%', transform: 'translateX(-50%)',
                                                bottom: reasonCodesOverlayOpen ? -(13 + reasonOverlayH) : -13, zIndex: 41,
                                                transition: 'bottom 0.42s cubic-bezier(0.4,0,0.2,1)',
                                            }}
                                        />
                                    </>
                                ) : (
                                    <BentoCard title={`Häufigste ${cm.secondaryAxisNounPlural}`}>
                                        {reasonCodeData.length > 0 ? (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                                {reasonCodeData.map(renderReasonRow)}
                                            </div>
                                        ) : (
                                            <div style={{ minHeight: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.colors.textSecondary, fontSize: 12 }}>
                                                {/* Hyphenated on purpose: the compound form takes the base noun, so this
                                                    site needs no morphology field and reads correctly under every role. */}
                                                Keine {cm.secondaryAxisNoun}-Daten
                                            </div>
                                        )}
                                    </BentoCard>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* ═══ Chatbot analytics ═══ real data via getChatbotAnalytics; scope-flip + date-range refetch.
                        Null metrics render "noch nicht erfasst" inside the card — never fabricated.
                        Fully independent of the H-G-C reveal (reasonCodesOverlayOpen) — neither collapses the other. */}
                    {chatbotAnalytics && (
                        <div style={{ position: 'relative', zIndex: 1, marginTop: 24 }}>
                            {/* Visually-hidden landmark heading — the card's own hero/labels carry the
                                visible hierarchy; this restores the semantic h2 (WCAG 1.3.1/2.4.6) that
                                the vanity section's visible "Chatbot" h2 provided pre-swap. */}
                            <h2 className="visually-hidden">Chatbot</h2>
                            <ChatbotAnalyticsCard
                                analytics={chatbotAnalytics}
                                expanded={chatbotExpanded}
                                onToggleExpanded={() => setChatbotExpanded(x => !x)}
                            />
                        </div>
                    )}

                    {/* ═══ Row 5: Export — lerp between text button and format circles ═══ */}
                    <div style={{
                        position: 'relative', display: 'flex', justifyContent: 'center',
                        alignItems: 'center', height: 56,
                    }}>
                        <button
                            onClick={() => setShowFormats(true)}
                            style={{
                                position: 'absolute',
                                display: 'inline-flex', alignItems: 'center', gap: 8,
                                padding: '12px 28px', fontSize: 13, fontWeight: 600,
                                fontFamily: theme.font.family, borderRadius: theme.borderRadius.sm,
                                border: `1px solid ${theme.colors.navy}`,
                                backgroundColor: theme.colors.white, color: theme.colors.navy,
                                cursor: 'pointer',
                                opacity: showFormats ? 0 : 1,
                                transform: showFormats ? 'scale(0.85)' : 'scale(1)',
                                pointerEvents: showFormats ? 'none' : 'auto',
                                transition: 'opacity 0.25s ease-out, transform 0.25s ease-out',
                            }}
                        >
                            <DownloadIcon size={14} color={theme.colors.navy} />
                            Bericht exportieren
                        </button>
                        <div data-card style={{
                            position: 'absolute',
                            display: 'flex', gap: 16, alignItems: 'center',
                            opacity: showFormats ? 1 : 0,
                            transform: showFormats ? 'scale(1)' : 'scale(0.85)',
                            pointerEvents: showFormats ? 'auto' : 'none',
                            transition: 'opacity 0.25s ease-out, transform 0.25s ease-out',
                        }}>
                            {/* PDF first (left), xlsx second — operator pref 2026-05-25.
                                PDF is the customer-facing executive artifact; xlsx is the
                                analyst follow-up. Reading-order matches that priority. */}
                            <button
                                disabled={exporting !== null && exporting !== 'pdf'}
                                onClick={() => exporting === 'pdf' ? handleCancelExport() : handleExport('pdf')}
                                title={exporting === 'pdf' ? 'Export abbrechen' : 'PDF (.pdf)'}
                                aria-label={exporting === 'pdf' ? 'PDF-Export abbrechen' : 'Als PDF exportieren'}
                                style={{
                                    width: 48, height: 48, borderRadius: '50%',
                                    border: 'none', backgroundColor: theme.colors.navy,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    cursor: (exporting !== null && exporting !== 'pdf') ? 'not-allowed' : 'pointer',
                                    opacity: exporting && exporting !== 'pdf' ? 0.4 : 1,
                                    transition: 'opacity 0.2s ease, transform 0.2s ease',
                                    transform: exporting === 'pdf' ? 'scale(0.92)' : 'scale(1)',
                                }}
                            >
                                {exporting === 'pdf'
                                    ? <span style={{ color: '#fff', fontSize: 14, fontWeight: 700, fontFamily: theme.font.family }}>×</span>
                                    : <PdfPageIcon size={30} />}
                            </button>
                            <button
                                disabled={exporting !== null && exporting !== 'xlsx'}
                                onClick={() => exporting === 'xlsx' ? handleCancelExport() : handleExport('xlsx')}
                                title={exporting === 'xlsx' ? 'Export abbrechen' : 'Excel (.xlsx)'}
                                aria-label={exporting === 'xlsx' ? 'Excel-Export abbrechen' : 'Als Excel exportieren'}
                                style={{
                                    width: 48, height: 48, borderRadius: '50%',
                                    border: 'none', backgroundColor: theme.colors.navy,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    cursor: (exporting !== null && exporting !== 'xlsx') ? 'not-allowed' : 'pointer',
                                    opacity: exporting && exporting !== 'xlsx' ? 0.4 : 1,
                                    transition: 'opacity 0.2s ease, transform 0.2s ease',
                                    transform: exporting === 'xlsx' ? 'scale(0.92)' : 'scale(1)',
                                }}
                            >
                                {exporting === 'xlsx'
                                    ? <span style={{ color: '#fff', fontSize: 14, fontWeight: 700, fontFamily: theme.font.family }}>×</span>
                                    : <ExcelIcon size={30} />}
                            </button>
                        </div>
                    </div>
                </>
            )}

            {/* Info toast — no undo button, auto-dismisses in 4 s */}
            <RueckgaengigToast
                message={infoToast.message}
                visible={infoToast.visible}
                onDismiss={() => setInfoToast(t => ({ ...t, visible: false }))}
            />
        </div>
    );
};

export default StatistikSeite;
